/**
 * Whitelabel Branding Service
 * 
 * Manages dynamic branding for multi-client whitelabel deployments.
 * Handles:
 * - Organization detection from URL/subdomain
 * - Branding asset loading and caching
 * - CSS variable application
 * - Feature visibility based on org configuration
 * 
 * Usage:
 *   import whitelabelBranding from '@/services/whitelabelBrandingService';
 * 
 *   // Load branding on app mount
 *   await whitelabelBranding.initialize();
 * 
 *   // Get branding values
 *   const logo = whitelabelBranding.getLogo();
 *   const primaryColor = whitelabelBranding.getColor('primary');
 * 
 *   // Check feature visibility
 *   if (whitelabelBranding.canShowFeature('carrier_analytics')) {
 *     // Show feature
 *   }
 */

import api from './api';
import auth from './auth';

class WhitelabelBrandingService {
  constructor() {
    this.currentBranding = null;
    this.currentOrganization = null;
    this.isInitialized = false;
    
    // Default branding (AccessIQ)
    this.defaultBranding = {
      logo_url: '/img/accessiq-logo-trimmed.png',
      logo_dark_url: '/img/accessiq-logo-trimmed.png',
      favicon_url: '/accessiq-favicon.png',
      app_name: 'AccessIQ',
      
      colors: {
        primary: '#212938',
        secondary: '#1a2130',
        accent: '#0059b2',
        background: '#ffffff',
        surface: '#ffffff',
        text: '#212938',
        text_light: '#6b7a99',
        error: '#DC2626',
        warning: '#F59E0B',
        success: '#10B981',
        info: '#3B82F6',
      },
      
      typography: {
        font_family: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        heading_font: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      },
      
      navigation: {
        show_powered_by: false,
        custom_footer_text: '',
        support_email: 'support@accessiq.health',
        support_phone: '',
      },
      
      features: {
        show_learning_module: true,
        show_carrier_analytics: true,
        show_rag_analysis: true,
        show_decision_center: true,
        show_data_suggestions: true,
      },
      
      oauth_providers: {
        google: { enabled: true },
        microsoft: { enabled: true },
        linkedin: { enabled: false },
        apple: { enabled: false },
      },
    };
  }

  /**
   * Initialize branding service
   * Detects organization and loads branding
   * 
   * @returns {Promise<void>}
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      // Detect organization from current context
      const orgSlug = this.detectOrganizationSlug();
      console.log('🔍 Detected organization slug:', orgSlug);
      
      if (orgSlug) {
        console.log('📡 Loading branding for organization:', orgSlug);
        await this.loadOrganizationBranding(orgSlug);
      } else {
        console.log('⚠️ No organization detected, using default branding');
        // Use default branding
        this.currentBranding = this.defaultBranding;
        this.applyBranding();
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize branding:', error);
      // Fallback to default
      this.currentBranding = this.defaultBranding;
      this.applyBranding();
      this.isInitialized = true;
    }
  }

  /**
   * Detect organization slug from various sources
   * 
   * Priority:
   * 1. URL parameter (?org=slug)
   * 2. Subdomain (slug.app.accessiq.health)
   * 3. LocalStorage (from previous login)
   * 4. User object (if authenticated)
   * 
   * @returns {string|null} Organization slug or null
   */
  detectOrganizationSlug() {
    // 1. Check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const orgParam = urlParams.get('org');
    if (orgParam) {
      return orgParam;
    }

    // 2. Check for custom domain mappings
    const hostname = window.location.hostname.toLowerCase();
    
    // Map custom domains to organizations
    if (hostname.includes('vetmediagroup.com') || hostname.includes('scav.org')) {
      console.log('🌐 Detected SCAV domain:', hostname);
      return 'colibri-media-group';
    }
    // Add more custom domain mappings here as needed
    // Example: if (hostname.includes('client.com')) return 'client-slug';

    // 3. Check subdomain
    const parts = hostname.split('.');
    
    // Assume format: {slug}.app.accessiq.health or {slug}.localhost
    if (parts.length >= 3 || (parts.length === 2 && hostname.includes('localhost'))) {
      const subdomain = parts[0];
      
      // Skip common subdomains
      if (!['www', 'app', 'api', 'admin', 'dev'].includes(subdomain)) {
        return subdomain;
      }
    }

    // 4. Check localStorage from previous session
    try {
      const storedOrg = localStorage.getItem('organization');
      if (storedOrg) {
        const org = JSON.parse(storedOrg);
        return org.slug;
      }
    } catch (e) {
      // Ignore parse errors
    }

    // 5. Check authenticated user's organization (4D: AuthService guards corrupt JSON)
    try {
      const user = auth.getUser();
      if (user?.organization?.slug) {
        return user.organization.slug;
      }
    } catch (e) {
      // Ignore
    }

    return null;
  }

  /**
   * Load branding for specific organization
   * 
   * @param {string} orgSlug - Organization slug
   * @returns {Promise<Object>} Branding configuration
   */
  async loadOrganizationBranding(orgSlug) {
    try {
      console.log(`📡 Fetching branding from /users/organizations/${orgSlug}/`);

      if (auth.getToken() && auth.getRefreshToken()) {
        await auth.ensureFreshAccessToken();
      }

      // Fetch organization details with branding
      const response = await api.get(`/users/organizations/${orgSlug}/`);
      
      console.log('✅ Organization data received:', response.data);
      console.log('🎨 Branding settings:', response.data.branding_settings);
      
      this.currentOrganization = response.data;
      
      // Transform backend branding format to frontend format
      const brandingSettings = response.data.branding_settings || {};
      
      // Map backend format (logos.primary) to frontend format (logo_url)
      if (brandingSettings.logos) {
        brandingSettings.logo_url = brandingSettings.logos.primary;
        brandingSettings.logo_dark_url = brandingSettings.logos.secondary;
        brandingSettings.favicon_url = brandingSettings.logos.favicon;
      }

      // Bust stale teal cache — old default was #0d9488, treat as unset
      if (brandingSettings.colors?.primary === '#0d9488') {
        delete brandingSettings.colors.primary;
      }
      if (brandingSettings.colors?.accent === '#14b8a6') {
        delete brandingSettings.colors.accent;
      }
      
      // Merge organization branding with defaults
      this.currentBranding = this.mergeBranding(
        this.defaultBranding,
        brandingSettings
      );
      
      console.log('🎨 Merged branding:', this.currentBranding);
      
      // Apply branding to DOM
      this.applyBranding();
      
      // Cache in localStorage
      this.cacheBranding(orgSlug, this.currentBranding);
      
      return this.currentBranding;
    } catch (error) {
      console.error(`❌ Failed to load branding for ${orgSlug}:`, error);
      
      // Try to use cached branding
      const cached = this.getCachedBranding(orgSlug);
      if (cached) {
        console.log('💾 Using cached branding');
        this.currentBranding = cached;
        this.applyBranding();
        return cached;
      }
      
      // Fallback to default
      console.log('⚠️ Using default branding as fallback');
      this.currentBranding = this.defaultBranding;
      this.applyBranding();
      throw error;
    }
  }

  /**
   * Deep merge branding objects
   * 
   * @param {Object} defaults - Default branding
   * @param {Object} custom - Custom branding overrides
   * @returns {Object} Merged branding
   */
  mergeBranding(defaults, custom) {
    const merged = { ...defaults };
    
    for (const key in custom) {
      if (custom[key] && typeof custom[key] === 'object' && !Array.isArray(custom[key])) {
        // Recursively merge objects
        merged[key] = {
          ...(merged[key] || {}),
          ...custom[key],
        };
      } else {
        // Override primitive values
        merged[key] = custom[key];
      }
    }
    
    return merged;
  }

  /**
   * Apply branding to the DOM
   * Updates CSS variables, favicon, page title, etc.
   */
  applyBranding() {
    const branding = this.currentBranding || this.defaultBranding;
    
    // Apply CSS variables for colors
    const root = document.documentElement;
    if (branding.colors) {
      Object.entries(branding.colors).forEach(([key, value]) => {
        // Convert camelCase to kebab-case (e.g., textLight -> text-light)
        const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        const cssVarName = `--color-${kebabKey}`;
        root.style.setProperty(cssVarName, value);
      });
    }
    
    // Apply custom CSS variables directly (overrides computed ones above)
    if (branding.cssVariables) {
      Object.entries(branding.cssVariables).forEach(([varName, value]) => {
        root.style.setProperty(varName, value);
      });
    }
    
    // Apply typography
    if (branding.typography) {
      if (branding.typography.font_family) {
        root.style.setProperty('--font-family', branding.typography.font_family);
      }
      if (branding.typography.heading_font) {
        root.style.setProperty('--font-heading', branding.typography.heading_font);
      }
    }
    
    // Update favicon (handle both single favicon_url and favicons object)
    if (branding.favicons && branding.favicons.favicon) {
      this.updateFavicon(branding.favicons.favicon);
      
      // Update other favicon types
      this.updateFaviconSet(branding.favicons);
    } else if (branding.favicon_url) {
      this.updateFavicon(branding.favicon_url);
    }
    
    // Update page title
    if (branding.app_name) {
      document.title = branding.app_name;
    }
  }

  /**
   * Update favicon
   * 
   * @param {string} faviconUrl - URL to favicon
   */
  updateFavicon(faviconUrl) {
    let link = document.querySelector('link[rel="icon"]');
    
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    
    link.href = faviconUrl;
  }

  /**
   * Update complete favicon set (ICO, SVG, Apple Touch Icon, etc.)
   * 
   * @param {Object} favicons - Favicon URLs object
   */
  updateFaviconSet(favicons) {
    // Update standard favicon (ICO)
    if (favicons.favicon) {
      let link = document.querySelector('link[rel="icon"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = favicons.favicon;
    }

    // Update SVG favicon (modern browsers)
    if (favicons.favicon_svg) {
      let svgLink = document.querySelector('link[rel="icon"][type="image/svg+xml"]');
      if (!svgLink) {
        svgLink = document.createElement('link');
        svgLink.rel = 'icon';
        svgLink.type = 'image/svg+xml';
        document.head.appendChild(svgLink);
      }
      svgLink.href = favicons.favicon_svg;
    }

    // Update Apple Touch Icon
    if (favicons.apple_touch_icon) {
      let appleLink = document.querySelector('link[rel="apple-touch-icon"]');
      if (!appleLink) {
        appleLink = document.createElement('link');
        appleLink.rel = 'apple-touch-icon';
        document.head.appendChild(appleLink);
      }
      appleLink.href = favicons.apple_touch_icon;
    }

    // Update 96x96 favicon
    if (favicons.favicon_96) {
      let favicon96 = document.querySelector('link[rel="icon"][sizes="96x96"]');
      if (!favicon96) {
        favicon96 = document.createElement('link');
        favicon96.rel = 'icon';
        favicon96.sizes = '96x96';
        document.head.appendChild(favicon96);
      }
      favicon96.href = favicons.favicon_96;
    }
  }

  /**
   * Cache branding in localStorage
   * 
   * @param {string} orgSlug - Organization slug
   * @param {Object} branding - Branding configuration
   */
  cacheBranding(orgSlug, branding) {
    try {
      const cacheKey = `branding_${orgSlug}`;
      const cacheData = {
        branding,
        timestamp: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Failed to cache branding:', e);
    }
  }

  /**
   * Get cached branding from localStorage
   * 
   * @param {string} orgSlug - Organization slug
   * @returns {Object|null} Cached branding or null
   */
  getCachedBranding(orgSlug) {
    try {
      const cacheKey = `branding_${orgSlug}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (cached) {
        const cacheData = JSON.parse(cached);
        
        // Cache for 24 hours
        const maxAge = 24 * 60 * 60 * 1000;
        if (Date.now() - cacheData.timestamp < maxAge) {
          return cacheData.branding;
        }
      }
    } catch (e) {
      // Ignore errors
    }
    
    return null;
  }

  /**
   * Get logo URL
   * 
   * @param {boolean} isDark - Whether to get dark mode logo
   * @returns {string} Logo URL
   */
  getLogo(isDark = false) {
    const branding = this.currentBranding || this.defaultBranding;
    let url;
    
    if (isDark && branding.logo_dark_url) {
      url = branding.logo_dark_url;
    } else {
      url = branding.logo_url;
    }

    // Old default was a 3706×1409 black canvas — dots vanish at sidebar size
    if (url === '/img/accessiq-logo.png' || url === '/accessiq-logo-transparent.png') {
      return '/img/accessiq-logo-trimmed.png';
    }
    
    return url;
  }

  /**
   * Get color value
   * 
   * @param {string} colorName - Color key (e.g., 'primary', 'secondary')
   * @returns {string} Color hex value
   */
  getColor(colorName) {
    const branding = this.currentBranding || this.defaultBranding;
    return branding.colors?.[colorName] || this.defaultBranding.colors[colorName];
  }

  /**
   * Get app name
   * 
   * @returns {string} App name
   */
  getAppName() {
    const branding = this.currentBranding || this.defaultBranding;
    return branding.app_name || this.defaultBranding.app_name;
  }

  /**
   * Check if "Powered by" should be shown
   * 
   * @returns {boolean} True if should show "Powered by Praetorian AI"
   */
  shouldShowPoweredBy() {
    const branding = this.currentBranding || this.defaultBranding;
    return branding.navigation?.show_powered_by !== false;
  }

  /**
   * Get custom footer text
   * 
   * @returns {string} Custom footer text or empty string
   */
  getFooterText() {
    const branding = this.currentBranding || this.defaultBranding;
    return branding.navigation?.custom_footer_text || '';
  }

  /**
   * Get support contact info
   * 
   * @returns {Object} Support email and phone
   */
  getSupportContact() {
    const branding = this.currentBranding || this.defaultBranding;
    return {
      email: branding.navigation?.support_email || 'support@accessiq.health',
      phone: branding.navigation?.support_phone || '',
    };
  }

  /**
   * Check if feature should be visible
   * 
   * @param {string} featureName - Feature key (e.g., 'show_carrier_analytics')
   * @returns {boolean} True if feature should be shown
   */
  canShowFeature(featureName) {
    const branding = this.currentBranding || this.defaultBranding;
    
    // Add 'show_' prefix if not present
    const key = featureName.startsWith('show_') ? featureName : `show_${featureName}`;
    
    // Default to true if not specified
    return branding.features?.[key] !== false;
  }

  /**
   * Check if OAuth provider is enabled
   * 
   * @param {string} provider - Provider name (e.g., 'google', 'microsoft')
   * @returns {boolean} True if provider is enabled
   */
  isOAuthProviderEnabled(provider) {
    const branding = this.currentBranding || this.defaultBranding;
    return branding.oauth_providers?.[provider]?.enabled !== false;
  }

  /**
   * Get current organization
   * 
   * @returns {Object|null} Current organization or null
   */
  getCurrentOrganization() {
    return this.currentOrganization;
  }

  /**
   * Get current branding
   * 
   * @returns {Object} Current branding configuration
   */
  getCurrentBranding() {
    return this.currentBranding || this.defaultBranding;
  }

  /**
   * Reset branding to default
   */
  reset() {
    this.currentBranding = null;
    this.currentOrganization = null;
    this.isInitialized = false;
    this.applyBranding();
  }

  /**
   * Force reload branding from server
   * 
   * @param {string} orgSlug - Optional organization slug
   * @returns {Promise<void>}
   */
  async reload(orgSlug = null) {
    this.isInitialized = false;
    
    const slug = orgSlug || this.detectOrganizationSlug();
    
    if (slug) {
      await this.loadOrganizationBranding(slug);
    } else {
      this.currentBranding = this.defaultBranding;
      this.applyBranding();
    }
    
    this.isInitialized = true;
  }

  /**
   * Get complete style object for inline styles
   * Useful for dynamic styling in components
   * 
   * @returns {Object} Style object with CSS properties
   */
  getInlineStyles() {
    const branding = this.currentBranding || this.defaultBranding;
    
    return {
      '--color-primary': branding.colors.primary,
      '--color-secondary': branding.colors.secondary,
      '--color-accent': branding.colors.accent,
      '--color-background': branding.colors.background,
      '--color-surface': branding.colors.surface,
      '--color-text': branding.colors.text,
      '--font-family': branding.typography.font_family,
    };
  }

  /**
   * Generate CSS class name based on current organization
   * Useful for organization-specific styling
   * 
   * @returns {string} CSS class name (e.g., 'org-acme-insurance')
   */
  getOrganizationClassName() {
    if (this.currentOrganization) {
      return `org-${this.currentOrganization.slug}`;
    }
    return 'org-default';
  }
}

// Export singleton instance
export default new WhitelabelBrandingService();

